import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { TelegramLogo } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import type { PublicBooking } from '@/lib/types';

const BOT = import.meta.env.VITE_TELEGRAM_BOT as string | undefined;

// A bot cannot write first: the client presses Start once via a one-time deep link,
// then gets a confirmation and (in a live studio) the 24h / 2h reminders in Telegram.
export function TelegramReminder({ booking, token }: { booking: PublicBooking; token: string }) {
  const qc = useQueryClient();
  const code = useQuery({
    queryKey: ['telegram-code', token],
    queryFn: () => api.clientTelegramLink(token),
    enabled: Boolean(BOT) && !booking.telegram_linked,
    staleTime: 20 * 60_000,
    retry: false,
  });

  if (!BOT) return null;
  if (booking.telegram_linked) {
    return (
      <Banner
        status="success"
        title="Telegram подключён"
        description={booking.reminders_enabled ? 'Напомним в Telegram за сутки и за 2 часа до визита.' : 'Подтверждение записи отправлено в Telegram.'}
        icon={<TelegramLogo size={20} />}
        collapsible={false}
      />
    );
  }
  if (code.isError) return null;
  return (
    <Button
      label={booking.reminders_enabled ? 'Напоминания в Telegram' : 'Получить запись в Telegram'}
      icon={<TelegramLogo size={18} />}
      width="100%"
      href={code.data ? `https://t.me/${BOT}?start=${code.data}` : undefined}
      target="_blank"
      rel="noopener noreferrer"
      isLoading={code.isPending}
      // After Start in Telegram the page should flip to «подключён» once she comes back.
      onClick={() => {
        const refresh = () => void qc.invalidateQueries({ queryKey: ['booking', token] });
        window.addEventListener('focus', refresh, { once: true });
        setTimeout(refresh, 8000);
      }}
    />
  );
}
